import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { useNations } from "../../hooks/useNations";
import { validateStep1 } from "../../utils/validators";
import { getReservationData } from "../../utils/functions";
import Popup from "../common/Popup";
import Input from "../common/Input";
import ButtonNext from "../ButtonNext";

const Step1 = ({
  bookingData,
  setBookingData,
  onComplete
}) => {
  const { t } = useTranslation();
  const nations = useNations();

  const [isLoading, setIsLoading] = useState(false);
  const [isButtonNextDisabled, setIsButtonNextDisabled] = useState(true);
  const [popupMessage, setPopupMessage] = useState("");

  const handleChange = (field, value) => {
    setBookingData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  }

  const handleNextStep = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setPopupMessage("");

    if (bookingData.type === "codice") {
      setBookingData((prevData) => ({
        type: prevData.type,
        code: prevData.code,
        date: bookingData.date
      }));
    }
    else if (bookingData.type === "voucher") {
      setBookingData((prevData) => ({
        type: prevData.type,
        code: prevData.code,
        name: prevData.name,
        surname: prevData.surname,
      }));
    }

    const res = await getReservationData(bookingData, nations);
    setIsLoading(false);

    if (!res || Object.keys(res).length === 0) {
      setPopupMessage(!res ? t("step1.errorDataNotFound") : t("step1.errorSendData"));
      return;
    }

    onComplete({
      reservation: res.reservation,
      customer: res.customer,
    });
  };

  useEffect(() => {
    setIsButtonNextDisabled(() => !validateStep1(t, bookingData).isValid);
  }, [t, bookingData]);

  return (
    <form className="step step--1" onSubmit={handleNextStep}>
      <Popup
        status="error"
        message={popupMessage}
        showPopup={popupMessage !== ""}
        onClose={() => setPopupMessage("")}
      />
      <h2 className="step__title">{t("step1.title")}</h2>
      <p className="step__subtitle">{t("step1.subtitle")}</p>
      <div className="step__content">
        <Input
          type="text"
          name="code"
          label={t("booking.code.label")}
          placeholder={t("booking.code.placeholder")}
          value={bookingData.code}
          disabled
        />
        {bookingData.type === "codice" && (
          <Input
            type="date"
            name="date"
            label={t("booking.date.label")}
            placeholder={t("booking.date.placeholder")}
            value={bookingData.date}
            datePickerMin={dayjs().subtract(5, "year").toDate()}
            datePickerMax={dayjs().add(5, "year").toDate()}
            onChange={handleChange}
            readOnly
          />
        )}
        {bookingData.type === "voucher" && (
          <>
            <Input
              type="text"
              name="name"
              label={t("booking.name.label")}
              placeholder={t("booking.name.placeholder")}
              value={bookingData.name || ""}
              onChange={handleChange}
            />
            <Input
              type="text"
              name="surname"
              label={t("booking.surname.label")}
              placeholder={t("booking.surname.placeholder")}
              value={bookingData.surname || ""}
              onChange={handleChange}
            />
          </>
        )}
      </div>
      <ButtonNext disabled={isButtonNextDisabled || isLoading} />
    </form>
  );
};

export default Step1;
